<template>
	<div
		class="fixed top-0 left-0 z-30 h-14 w-full print:hidden"
		:class="[showProgress ? 'bg-white' : 'pointer-events-none bg-transparent']"
	>
		<header
			class="grid h-full w-full grid-cols-[max-content_1fr_max-content_max-content] grid-rows-[56px]"
		>
			<!-- Wrapper makes a space for the button which itself is fixed -->
			<div class="pointer-events-auto w-20">
				<button
					type="button"
					class="fixed top-2 left-4 z-50"
					@click="isOpen = !isOpen"
					data-test-id="presentation-nav-menu-button"
				>
					<Bars3Icon class="fill-secondary-900 h-10 w-10" />
				</button>
			</div>

			<nav
				class="text-secondary-900 pointer-events-auto flex h-full flex-row items-center space-x-4 justify-self-center"
				v-if="showProgress"
			>
				<button
					type="button"
					class="h-full border-y-[3px] border-transparent font-sans text-xs font-medium tracking-wide uppercase"
					:class="{
						'border-b-primary text-primary-700':
							presentation.activeSlide?._uid === slide._uid,
					}"
					v-for="slide in presentation.activeChapter?.slides"
					:key="slide.title"
					@click="gotoSlide(slide._uid)"
				>
					{{ slide.title }}
				</button>
			</nav>

			<!-- Overlay buttons -->
			<div class="flex items-center">
				<!-- List View Icon -->
				<button
					v-if="showListView"
					type="button"
					@click="
						showOverlay = showOverlay === 'listView' ? 'none' : 'listView'
					"
					class="pointer-events-auto relative z-30 px-2"
					:class="
						showOverlay === 'listView'
							? 'border-primary-700 border-b last:border-b-0'
							: ''
					"
				>
					<TableCellsIcon class="text-primary-700 h-6 w-6" />
				</button>

				<!-- GPL Document Icon -->
				<button
					v-if="showGPL && !!gplDocumentUrl"
					type="button"
					data-test-id="presentation-nav-show-gpl-button"
					@click="showOverlay = showOverlay === 'gpl' ? 'none' : 'gpl'"
					class="pointer-events-auto relative z-30 px-2"
					:class="
						showOverlay === 'gpl' ? 'border-primary-700 border-b-[3px]' : ''
					"
				>
					<DocumentIcon
						class="text-primary-700 h-6 w-6"
						v-if="showOverlay === 'gpl'"
					/>
					<DocumentIconOutline
						class="text-primary-700 h-6 w-6"
						v-else
					/>
				</button>
			</div>

			<!-- Avatars -->
			<button
				v-if="showAvatars"
				type="button"
				data-test-id="presentation-nav-avatars-button"
				class="pointer-events-auto relative z-30 mx-5 flex cursor-pointer flex-row items-center space-x-3 justify-self-end"
				@click="toggleOverlay"
			>
				<PlanAvatar
					v-for="(plan, index) in sessionStore.activePlans"
					:key="index"
					:index="index"
					class="text-md! size-9"
					filled
					:plan="plan"
				/>
			</button>
		</header>

		<!-- Pull-out menu -->
		<Transition
			class="transition-all duration-300"
			enter-from-class="-left-72"
			enter-to-class="left-0"
			leave-from-class="left-0"
			leave-to-class="-left-72"
		>
			<nav
				v-if="isOpen"
				ref="pullOutMenu"
				class="bg-background-50 pointer-events-auto absolute top-0 z-40 flex h-dvh w-72 flex-col place-content-between"
			>
				<!-- Chapter List -->
				<ol class="mt-24 divide-y divide-slate-200">
					<MenuItem
						v-for="chapter in presentation.chapters"
						:key="chapter.title"
						@click="gotoChapter(chapter._uid)"
						:active="presentation.activeChapter?.title === chapter.title"
					>
						<div v-if="chapter.title">
							{{ chapter.title }}
						</div>
					</MenuItem>
				</ol>

				<!-- Footer links -->
				<ol class="divide-y divide-slate-200">
					<MenuItem
						v-if="!usedInFdl"
						@click="openDocuments"
					>
						{{ t('presentation.forms') }}
						<template #icon>
							<DocumentTextIcon
								class="group-hover/menuitem:text-accent-500 h-5 w-5 text-slate-500"
							/>
						</template>
					</MenuItem>
					<MenuItem @click="showPersonModal = true">
						{{ t('presentation.addEditRemovePerson') }}
						<template #icon>
							<UserIcon
								class="group-hover/menuitem:text-accent-500 h-5 w-5 text-slate-500"
							/>
						</template>
					</MenuItem>
					<!-- Print Multiple Slides (Memorial Guide, Review, & Payment Options) -->
					<MenuItem @click="printMultipleSlides">
						{{ t('presentation.memorialGuideAndEstimates') }}
						<template #icon>
							<PrinterIcon
								class="group-hover/menuitem:text-accent-500 h-5 w-5 text-slate-500"
							/>
						</template>
					</MenuItem>
					<MenuItem @click="enterFullscreen">
						{{ t('presentation.fullscreen') }}
						<template #icon>
							<ArrowsPointingOutIcon
								class="group-hover/menuitem:text-accent-500 h-5 w-5 text-slate-500"
							/>
						</template>
					</MenuItem>
					<MenuItem
						@click="exitPresentation"
						data-test-id="presentation-nav-exit"
					>
						{{ t('presentation.exit') }}
					</MenuItem>
				</ol>
			</nav>
		</Transition>

		<!-- ListView Overlay -->

		<Transition
			class="transition-all duration-300"
			enter-from-class="opacity-0"
			enter-to-class="opacity-100"
			leave-from-class="opacity-100"
			leave-to-class="opacity-0"
		>
			<div
				class="pointer-events-auto fixed top-0 right-0 bottom-0 left-0 z-20 flex justify-center bg-white px-4 pt-24"
				v-if="showOverlay === 'listView'"
				data-test-id="show-list-view"
			>
				<OverlayListView />
			</div>
		</Transition>

		<!-- GPL PDF Viewer -->
		<Transition
			class="transition-all duration-300"
			enter-from-class="opacity-0"
			enter-to-class="opacity-100"
			leave-from-class="opacity-100"
			leave-to-class="opacity-0"
		>
			<div
				class="pointer-events-auto fixed top-0 right-0 bottom-0 left-0 z-20 flex items-stretch justify-center overflow-hidden bg-white"
				v-if="showOverlay === 'gpl'"
				data-test-id="show-gpl-pdf-viewer"
			>
				<iframe
					class="mt-14 flex w-full flex-col items-center justify-center gap-12"
					:src="gplDocumentUrl"
					type="application/pdf"
				></iframe>
			</div>
		</Transition>

		<!-- Most Important Reason Overlay -->
		<Transition
			class="transition-all duration-300"
			enter-from-class="opacity-0"
			enter-to-class="opacity-100"
			leave-from-class="opacity-100"
			leave-to-class="opacity-0"
		>
			<div
				class="pointer-events-auto fixed top-0 right-0 bottom-0 left-0 z-20 flex items-stretch justify-center overflow-hidden bg-white pt-14"
				v-if="showOverlay === 'mostImportantReason'"
			>
				<div
					class="flex w-full flex-col items-center justify-center gap-12"
					v-if="showHeadingAndTextInput"
				>
					<SbHeadingAndTextInput :blok="mostImportantReason" />
					<button
						type="button"
						class="bg-accent-400 text-accent-700 hover:bg-accent-700 rounded-lg border-0 px-40 py-2.5 text-base font-bold hover:text-white"
						@click="showOverlay = 'none'"
					>
						{{ t('presentation.returnToPresentation') }}
					</button>
				</div>
				<div
					class="flex w-full flex-col justify-center"
					v-else
				>
					<button
						type="button"
						@click="showHeadingAndTextInput = !showHeadingAndTextInput"
						class="mx-7 my-4 self-end"
					>
						<div class="flex items-center justify-start gap-2">
							<span class="bg-accent-400 rounded-full p-1">
								<PencilIcon class="h-2.5 w-2.5 text-xs text-white" />
							</span>
							<div class="text-accent-500 font-sans text-base font-bold">
								{{ t('presentation.edit') }}
							</div>
						</div>
					</button>

					<CardQuoteList
						:plans="sessionStore.activePlans"
						class="h-full items-start self-center"
					/>
				</div>
			</div>
		</Transition>

		<PrecoaModal
			v-model:open="showPersonModal"
			@close="showPersonModal = false"
			size="large"
		>
			<AddRemovePersonModal
				@save="handleSavePersons"
				@cancel="showPersonModal = false"
			/>
		</PrecoaModal>
	</div>
</template>

<script setup lang="ts">
import SbHeadingAndTextInput from '../blocks/SbHeadingAndTextInput.vue'
import OverlayListView from './OverlayListView.vue'
import Plan from '@/classes/Plan'
import MenuItem from '@/components/common/MenuItem.vue'
import PrecoaModal from '@/components/common/PrecoaModal.vue'
import AddRemovePersonModal from '@/components/presentation/AddRemovePersonModal.vue'
import CardQuoteList from '@/components/presentation/CardQuoteList.vue'
import PlanAvatar from '@/components/presentation/PlanAvatar.vue'
import { usePresentationLocale } from '@/i18n'
import { initializePlanOap } from '@/mixins/oapPlanProvider'
import type { HeadingAndTextInputStoryblok } from '@/sbTypes'
import { useAssetStore } from '@/stores/asset'
import { usePlanStore } from '@/stores/plan'
import { usePresentationController } from '@/stores/presentationController'
import { useSessionStore } from '@/stores/session'
import {
	ArrowsPointingOutIcon,
	DocumentIcon as DocumentIconOutline,
	TableCellsIcon,
} from '@heroicons/vue/24/outline'
import {
	Bars3Icon,
	DocumentIcon,
	DocumentTextIcon,
	PencilIcon,
	PrinterIcon,
	UserIcon,
} from '@heroicons/vue/24/solid'
import { captureException } from '@sentry/vue'
import { computedAsync, onClickOutside } from '@vueuse/core'
import { computed, inject, ref, watch } from 'vue'
import { useRoute, useRouter } from 'vue-router'

const plansStore = usePlanStore()
const route = useRoute()
const router = useRouter()

const { t } = usePresentationLocale()

const usedInFdl = inject('isFdl')

const isOpen = ref(false)
const sessionStore = useSessionStore()
const asset = useAssetStore()
const presentation = usePresentationController()

const showProgress = computed(() => presentation.activeChapter?.show_slide_nav)
const showAvatars = computed(
	() => presentation.activeChapter?.show_customer_avatars
)
const showGPL = computed(() => presentation.activeChapter?.show_gpl)

const showListView = computed(() => presentation.activeChapter?.show_list_view)

const showPersonModal = ref(false)
const pullOutMenu = ref<HTMLElement | null>(null)
const showOverlay = ref<'none' | 'mostImportantReason' | 'gpl' | 'listView'>(
	'none'
)
const showHeadingAndTextInput = ref(false)

const gplDocumentUrl = computedAsync<string | undefined>(() => {
	if (!showGPL.value) return undefined
	const url = sessionStore.activePriceBook?.data.externalGplUrl
	return url ? asset.getAssetBlobUrl(url) : undefined
})

/**
 * Returns the most important reason component from the presentation.
 * @returns {HeadingAndTextInputStoryblok} The most important reason component
 */
const mostImportantReason = computed(() => {
	// Loop through the slides and find the 'mostImportantReason' component
	for (const slide of presentation.allSlides ?? []) {
		if ('content' in slide && Array.isArray(slide.content)) {
			for (const component of slide.content) {
				if (
					component.component === 'heading-and-text-input' &&
					(component as HeadingAndTextInputStoryblok).plan_field ===
						'mostImportantReason'
				) {
					// Return the component as HeadingAndTextInputStoryblok
					return component as HeadingAndTextInputStoryblok
				}
			}
		}
	}
	// Return undefined if no matching component is found
	return undefined
})

// Close the nav menu when the user clicks outside of it (ignore clicks inside the person modal)
onClickOutside(pullOutMenu, () => {
	isOpen.value = false
})

// Close the nav menu when the user navigates to a new slide
watch(
	() => presentation.activeSlideId,
	() => {
		isOpen.value = false
	}
)

// Get an existing leadId or null if none exist
function getExistingLeadId(plans: { leadId?: string | null }[]) {
	return plans.find((p) => p.leadId)?.leadId ?? null
}

// Update, create, or remove plans
async function handleSavePersons(updatedPeople: any[]) {
	const session = sessionStore.activeSession
	if (!session) return

	const currentPlans = sessionStore.activePlans
	const leadId = getExistingLeadId(currentPlans)

	const newPlans: Plan[] = []

	// Update or create plans
	for (const person of updatedPeople) {
		const existing = person.id
			? currentPlans.find((p) => p.id === person.id)
			: undefined

		if (existing) {
			Object.assign(existing, person, { leadId })
			await plansStore.savePlan(existing)

			newPlans.push(existing)
		} else {
			const created = new Plan({
				...person,
				leadId,
				funeralHomeId: currentPlans[0]?.funeralHomeId,
			})

			initializePlanOap(created)

			await plansStore.addPlan(created)

			newPlans.push(created)
		}
	}

	const updatedPlanIds = newPlans.map((p) => p.id)

	// Update session
	session.planIds = updatedPlanIds
	await sessionStore.saveActiveSession()

	showPersonModal.value = false
}

// Enter fullscreen mode and close the pull-out menu
// While transtioning disable swiper navigation and tab key
async function enterFullscreen() {
	const swiper = presentation.swiper
	if (!swiper) return

	isOpen.value = false

	swiper.allowSlideNext = false
	swiper.allowSlidePrev = false

	function blockTab(e: KeyboardEvent): void {
		if (e.key === 'Tab') e.preventDefault()
	}

	document.addEventListener('keydown', blockTab, true)

	try {
		await document.body.requestFullscreen()
		await new Promise((resolve) => setTimeout(resolve, 800))

		swiper.update()
	} catch (error) {
		captureException(error, {
			extra: { message: 'Error attempting to enable fullscreen' },
		})
		console.error('Error attempting to enable fullscreen:', error)
	} finally {
		swiper.allowSlideNext = true
		swiper.allowSlidePrev = true
		document.removeEventListener('keydown', blockTab, true)
	}
}

/**
 * Opens a new window and prints memorial guide, review slide, and the payment options slide (if available)
 */
function printMultipleSlides() {
	router.push({
		name: 'print-multiple-slides',
		params: { sessionId: route.params.sessionId },
	})
}

/**
 * Navigates to the documents page with the current session id
 */
function openDocuments() {
	router.push({
		name: 'documents',
		params: { sessionId: route.params.sessionId },
	})
}

/**
 * Toggles the display state of the overlay and its content.
 * If the overlay is showing the MIR inputs, show the cards instead.
 * If the overlay is showing cards, hide everything.
 * If there are no cards available, show the inputs.
 * Otherwise, show the overlay and the cards.
 */
function toggleOverlay() {
	const noCardsAvailable =
		sessionStore.activePlans?.filter((plan) => plan.mostImportantReason)
			?.length === 0

	if (
		showOverlay.value === 'mostImportantReason' &&
		showHeadingAndTextInput.value
	) {
		// If overlay is showing and inputs are displayed, show cards instead
		showHeadingAndTextInput.value = false

		// If no cards are available, hide the overlay
		if (noCardsAvailable) {
			showOverlay.value = 'none'
		}
	} else if (
		showOverlay.value === 'mostImportantReason' &&
		showHeadingAndTextInput.value === false
	) {
		// If overlay and cards are showing, hide everything
		showOverlay.value = 'none'
	} else if (noCardsAvailable) {
		// If there are no cards available, show the inputs
		showHeadingAndTextInput.value = true
		showOverlay.value = 'mostImportantReason'
	} else {
		// Show the overlay with the cards
		showHeadingAndTextInput.value = false
		showOverlay.value = 'mostImportantReason'
	}
}

/** Navigates to the given slide by id */
function gotoSlide(id: string) {
	showOverlay.value = 'none'
	presentation.gotoSlideId(id)
}

/** Navigates to the given chapter by id */
function gotoChapter(id: string) {
	showOverlay.value = 'none'
	isOpen.value = false
	presentation.gotoChapterId(id)
}

function exitPresentation() {
	if (usedInFdl) {
		if (document.fullscreenElement) {
			document
				.exitFullscreen()
				.catch(() => console.error('cannot exit full screen'))
		}

		router.push({ name: 'fdl-embed-v1' })
	} else {
		presentation.exit()
	}
}

// hide overlay when presentation active slide changes.
watch(
	() => presentation.activeSlideId,
	() => {
		showOverlay.value = 'none'
	}
)

/** Expose properties for unit tests */
defineExpose({
	gplDocumentUrl,
})
</script>

<style scoped>
ol > li:first-child {
	border-top: 1px solid #e2e8f0;
}
</style>
